import { CSSProperties, useCallback, useEffect, useState } from "react";

export type StyleConfig = {
  appWrapperStyle: CSSProperties;
  modalStyle: CSSProperties;
  iframeWrapperStyle: CSSProperties;
  iframeStyle: CSSProperties;
};

/**
 * Hook to manage style state and updates from postMessage events.
 *
 * Encapsulates:
 * - Style state management (current styles and loaded status)
 * - Style merging logic via postMessage
 * - Origin validation
 * - Fallback timeout to ensure modal displays even if STYLE_CONFIG is not received
 *
 * @param zeroHashAppURL - The app URL used for origin validation
 * @param defaultStyles - Default style configuration to use as fallback
 * @returns Object containing current styles, loaded status, and handler function
 */
export const useStyleUpdates = (
  zeroHashAppURL: string,
  defaultStyles: StyleConfig,
) => {
  const [styles, setStyles] = useState<StyleConfig>(defaultStyles);
  const [stylesLoaded, setStylesLoaded] = useState(false);

  /**
   * Fallback timeout to ensure modal is displayed even if STYLE_CONFIG postMessage
   * is not received or the app crashes before sending it. After 4.5 seconds,
   * if stylesLoaded is still false, we mark it as true to show the modal with default styles.
   */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setStylesLoaded((prevState) => prevState || true);
    }, 4500);

    return () => clearTimeout(timeoutId);
  }, []);

  const handleStyleConfig = useCallback(
    (incomingStyles: Partial<StyleConfig>, eventOrigin: string): boolean => {
      const zhAppsURLOrigin = new URL(zeroHashAppURL).origin;
      /**
       * Here we have 2 scenarios, one where the postMessage comes from sdk-ui-apps and another one where
       * the postMessage comes from Connect (Auth).
       * 1. In the first scenario, the eventOrigin will always be the same as zhAppsURLOrigin, which is expected and valid.
       * 2. In the second scenario, the eventOrigin will always be the same as window.location.origin, which is also expected
       * and valid since Connect (Auth) is served from the same origin as the main app (no iframe).
       */
      const isValidOrigin =
        eventOrigin === zhAppsURLOrigin ||
        eventOrigin === window.location.origin;

      if (!isValidOrigin) {
        return false;
      }

      setStyles((prev) => ({
        appWrapperStyle: incomingStyles.appWrapperStyle
          ? { ...prev.appWrapperStyle, ...incomingStyles.appWrapperStyle }
          : prev.appWrapperStyle,
        modalStyle: incomingStyles.modalStyle
          ? { ...prev.modalStyle, ...incomingStyles.modalStyle }
          : prev.modalStyle,
        iframeWrapperStyle: incomingStyles.iframeWrapperStyle
          ? { ...prev.iframeWrapperStyle, ...incomingStyles.iframeWrapperStyle }
          : prev.iframeWrapperStyle,
        iframeStyle: incomingStyles.iframeStyle
          ? { ...prev.iframeStyle, ...incomingStyles.iframeStyle }
          : prev.iframeStyle,
      }));
      setStylesLoaded(true);
      return true;
    },
    [zeroHashAppURL],
  );

  return { styles, stylesLoaded, handleStyleConfig };
};
